import { useEffect, useState } from "react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CategoriesSubnav from "@/components/CategoriesSubnav";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { ArrowRight, CircleCheck, Leaf, ShieldCheck, Lock, Award } from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const PRODUCT_SLUGS = [
  "papier-bulle-1x100m",
  "papier-bulle-05x100m",
  "papier-bulle-predecoupe-05x100m",
  "papier-bulle-10m",
  "bulle-kraft-125x100m",
  "film-etirable",
  "housse-matelas-1-place",
  "housse-matelas-2-places",
  "housse-matelas-king-size",
  "papier-blanc-journal",
  "couverture-demenagement",
];

const EmballageProtection = () => {
  useDocumentTitle(
    "Emballage et protection déménagement | cartonmove",
    "Papier bulle, film étirable, housses matelas, papier blanc journal, couvertures de déménagement. Protection optimale de vos affaires à Paris et en Île-de-France."
  );

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts()
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const items = PRODUCT_SLUGS.map((s) => products.find((p) => p.slug === s)).filter(Boolean);

  return (
    <div data-testid="emballage-protection-page">
      <CategoriesSubnav />

      {/* TOP — H1 + produits + intro */}
      <section className="section-pad bg-white" id="produits">
        <div className="container-cdm">
          <div className="max-w-3xl mx-auto text-center">
            <div className="overline justify-center">Boutique cartonmove</div>
            <h1 className="mt-3 text-[28px] sm:text-[33px] md:text-[48px] font-extrabold text-[#0f1d2b] leading-tight">
              Emballage et protection déménagement
            </h1>
          </div>

          {loading ? (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-square bg-zinc-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="emballage-grid">
              {items.map((p, idx) => (
                <ProductCard key={p.id} product={p} index={idx} hideQty ctaLabel="Acheter" />
              ))}
            </div>
          )}

          {/* Intro */}
          <div className="mt-12 max-w-3xl mx-auto text-center">
            <p className="text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Retrouvez toute notre gamme de <strong>moyens de protections et emballages</strong> pour
              préserver, stabiliser et acheminer vos biens&nbsp;: des solutions professionnelles et
              fiables pour garantir l'intégrité de vos effets personnels et professionnels. Des
              fournitures indispensables pour organiser un déménagement à <strong>Paris</strong>, en{" "}
              <strong>Île-de-France</strong>, que ce soit en appartement, pavillon ou locaux d'entreprise.
            </p>
          </div>

          {/* CTA Big Déménagement */}
          <div className="mt-12">
            <BigDemenageurCTA testId="emballage-bigdem-cta" />
          </div>

          {/* LIVRAISON IDF */}
          <div className="mt-12 max-w-4xl mx-auto bg-white border border-[#e6edf3] rounded-2xl p-6 sm:p-8 lg:p-10">
            <div className="overline">Service de livraison</div>
            <h2 className="mt-3 text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Livraison de moyens de protections et emballage en Île-de-France
            </h2>
            <p className="mt-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Notre entreprise prend en charge l'acheminement de vos fournitures et matériels de
              conditionnement, exclusivement sur la région parisienne.
            </p>
            <p className="mt-3 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Pour s'adapter à votre emploi du temps, notre service logistique valide avec vous un
              rendez-vous précis selon votre planning. Vos équipements de sécurité arrivent au moment
              convenu, sans stress et avec une efficacité maximale pour vos préparatifs.
            </p>

            <h3 className="mt-6 text-base font-extrabold text-[#0f1d2b]">Nos engagements de livraison&nbsp;:</h3>
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5" data-testid="emballage-livraison-highlights">
              {[
                "Transport assuré par nos propres agents",
                "Couverture exclusive de la région Île-de-France",
                "Rendez-vous planifié selon votre calendrier",
                "Fournitures déposées sur votre lieu de vie ou de travail",
              ].map((h) => (
                <li key={h} className="flex items-start gap-2.5 text-[15px] text-[#0f1d2b] leading-relaxed">
                  <CircleCheck className="h-5 w-5 mt-0.5 shrink-0 text-[#237cb5]" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SEO sections */}
      <section className="section-pad bg-[#EEF3F7] border-t border-[#e6edf3]">
        <div className="container-cdm max-w-4xl space-y-10">
          {/* Combien de rouleaux d'adhésif */}
          <article>
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Combien de rouleaux d'adhésif pour un déménagement&nbsp;?
            </h2>
            <div className="mt-5 space-y-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              <p>
                En moyenne, prévoyez <strong>1 rouleau de 66 mètres</strong> pour fermer 15 à 20
                cartons standards. Nous vous conseillons d'ajouter un rouleau supplémentaire à votre
                panier pour fixer vos films bulles et housses de protection. Vous éviterez ainsi toute
                mauvaise surprise le jour J.
              </p>
            </div>
          </article>

          {/* Couvertures vs draps */}
          <article className="bg-white rounded-2xl border border-[#e6edf3] p-6 sm:p-8 lg:p-10">
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Couvertures de déménagement ou simples draps&nbsp;?
            </h2>
            <div className="mt-5 space-y-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              <p>
                Il est essentiel de choisir des <strong>couvertures spéciales déménagement</strong>&nbsp;:
                elles possèdent une épaisseur renforcée unique qui amortit les chocs et stoppe les
                rayures. Contrairement aux draps classiques qui se déchirent au moindre frottement,
                leur <strong>tissu technique ultra-résistant</strong> protège efficacement votre
                mobilier lourd contre les impacts dans le camion et les angles de murs dans les escaliers.
              </p>
            </div>
          </article>

          {/* Pourquoi nos matériels en IDF */}
          <article>
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Pourquoi choisir nos matériels de protection en IDF&nbsp;?
            </h2>
            <div className="mt-5 space-y-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              <p>
                Nous vous proposons exclusivement des fournitures de{" "}
                <strong>qualité professionnelle</strong>, testées et approuvées pour résister aux
                déménagements les plus exigeants. En combinant la robustesse de nos produits à notre{" "}
                <strong>livraison sur-mesure en Île-de-France</strong>, vous bénéficiez de la solution
                idéale pour sécuriser vos biens au meilleur prix.
              </p>
            </div>
          </article>

          {/* Démarche éco-responsable */}
          <article className="bg-white rounded-2xl border border-[#e6edf3] p-6 sm:p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#eaf3fb] text-[#237cb5]">
                <Leaf className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
                Notre démarche éco-responsable
              </h2>
            </div>
            <div className="mt-3 space-y-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              <p>
                Soucieux de l'environnement, nous sélectionnons des{" "}
                <strong>fournitures de protection recyclées, recyclables ou biodégradables</strong>,
                comme notre papier kraft et nos adhésifs éco-conçus. Préparez ainsi votre transition
                vers votre nouveau lieu de vie ou de travail tout en limitant votre empreinte écologique.
              </p>
            </div>
          </article>

          {/* Garanties de confiance */}
          <article>
            <div className="flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#eaf3fb] text-[#237cb5]">
                <ShieldCheck className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
                Vos garanties de confiance
              </h2>
            </div>
            <div className="mt-3 space-y-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              <p>
                Commandez vos équipements l'esprit tranquille grâce à notre système de{" "}
                <strong>paiement 100 % sécurisé</strong>. Nous vous garantissons un matériel de qualité
                professionnelle et une totale transparence sur le suivi de votre commande.
              </p>
            </div>

            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: Lock, text: "Paiement 100 % sécurisé" },
                { icon: Award, text: "Qualité professionnelle" },
                { icon: CircleCheck, text: "Transparence sur le suivi" },
              ].map(({ icon: Icon, text }) => (
                <li
                  key={text}
                  className="flex items-start gap-3 bg-white rounded-xl border border-[#e6edf3] p-4"
                >
                  <span className="flex items-center justify-center h-9 w-9 rounded-full bg-[#eaf3fb] text-[#237cb5] shrink-0">
                    <Icon className="h-4 w-4" strokeWidth={2.4} />
                  </span>
                  <span className="text-[14.5px] text-[#0f1d2b] leading-[1.6] font-semibold mt-1">
                    {text}
                  </span>
                </li>
              ))}
            </ul>
          </article>
        </div>
      </section>
    </div>
  );
};

export default EmballageProtection;
